'use client'

import { CollectionContainer } from '@/components/common/collection/collection-container'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useState } from 'react'

interface OutfitHighlight {
  image: string
  title: string
  description: string
}

export const RacquetSportsOutfitHighlights = () => {
  const t = useTranslations()
  const isMobile = useIsMobileClient()
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null)

  const outfitHighlights: OutfitHighlight[] = [
    {
      image: '/image/2026/08/13/Knit_-_mesh.jpg',
      title: t.raw('badminton_hardcode.outfit_highlights.breathable.title'),
      description: t(
        'badminton_hardcode.outfit_highlights.breathable.description'
      ),
    },
    {
      image: '/image/2026/08/06/Ergnomic_Panel.jpg',
      title: t.raw('badminton_hardcode.outfit_highlights.movement.title'),
      description: t(
        'badminton_hardcode.outfit_highlights.movement.description'
      ),
    },
    {
      image: '/image/2026/08/06/Functional_Pocket.jpg',
      title: t.raw('badminton_hardcode.outfit_highlights.pocket.title'),
      description: t('badminton_hardcode.outfit_highlights.pocket.description'),
    },
    {
      image: '/image/2026/08/13/Seam__Edge_Tech_Finish.jpg',
      title: t.raw('badminton_hardcode.outfit_highlights.seam.title'),
      description: t('badminton_hardcode.outfit_highlights.seam.description'),
    },
  ]

  // Auto play effect
  useEffect(() => {
    if (!carouselApi || outfitHighlights.length < 2) return
    const interval = setInterval(() => {
      if (carouselApi) {
        carouselApi.scrollNext()
      }
    }, 3000) // 3 seconds
    return () => clearInterval(interval)
  }, [carouselApi, outfitHighlights.length])

  return (
    <section className='bg-neutral-900 py-6 md:py-15'>
      <CollectionContainer className='bg-neutral-900'>
        <Carousel
          opts={{
            align: 'start',
            loop: true,
            skipSnaps: false,
            slidesToScroll: 1,
          }}
          setApi={setCarouselApi}
          className='w-full'
        >
          <CarouselContent className='-ml-4'>
            {outfitHighlights.map((outfitHighlight, index) => (
              <CarouselItem
                key={index}
                className={cn(
                  'group cursor-pointer pl-4',
                  isMobile ? 'basis-[66.666%]' : 'basis-1/4'
                )}
              >
                <div className='relative overflow-hidden rounded-xl md:rounded-2xl'>
                  <div className='aspect-[435/558]'>
                    <Image
                      src={ImageHelper.getImageUrlCdn(outfitHighlight.image)}
                      alt={outfitHighlight.title.replace(/<[^>]*>/g, ' ')}
                      fill
                      className='h-full w-full object-cover transition-all duration-500 ease-in-out group-hover:scale-105'
                    />
                  </div>
                  <div className='absolute bottom-0 left-0 flex w-full flex-col justify-end gap-1 bg-gradient-to-t from-black/70 to-transparent p-4 pb-3 text-white md:p-6'>
                    <h3
                      className='text-base font-medium uppercase text-white md:text-2xl'
                      dangerouslySetInnerHTML={{
                        __html: outfitHighlight.title,
                      }}
                    />
                    <p className='text-xs text-white/90 md:text-base'>
                      {outfitHighlight.description}
                    </p>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          <CarouselNavigation />
        </Carousel>
      </CollectionContainer>
    </section>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-3 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-white p-2 text-black md:-left-5 md:p-3'
        variant='outline'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-3 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-white p-2 text-black md:-right-5 md:p-3'
        variant='outline'
        hideIfEnd
      />
    </>
  )
}
